import { AnimatePresence, motion } from 'framer-motion';
import { useLocation, Routes, Route } from 'react-router-dom';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from './contexts/LanguageContext';
import HomePage from './pages/HomePage';
import ProductPage from './pages/ProductPage';
import ReviewPage from './pages/ReviewPage';
import AllReviewsPage from './pages/AllReviewsPage';
import ProductGrid from './components/ProductGrid';
import Header from './components/Header';
import Footer from './components/Footer';
import { CartProvider } from './components/CartContext';
import CartSidebar from './components/CartSidebar';
import CookieConsent from './components/CookieConsent';
import SearchSidebar from './components/SearchSidebar';
import CheckoutPage from './pages/CheckoutPage';
import OrderSuccessPage from './pages/OrderSuccessPage';
import MobileMenu from './components/MobileMenu';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { ShippingPage, TermsPage, ReturnsPage, LegalPage } from './pages/FooterLinksPage.jsx';
import { PaymentSuccessPage, PaymentFailedPage } from './pages/PaymentPages.jsx';
import OrderReceived from './pages/OrderRecieved';
import NotFoundPage from './pages/NotFoundPage'; // You'll need to create this component

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0
  },
  in: {
    opacity: 1
  },
  out: {
    opacity: 0
  }
};

const pageTransition = {
  type: 'tween',
  ease: 'easeOut',
  duration: 0.3
};

// Custom hook for updating document title with localization and debugging
const useDocumentTitle = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { currentLanguage } = useLanguage();

  useEffect(() => {
    // Debug logging
    console.log('Current language:', currentLanguage);
    console.log('i18n language:', i18n.language);
    console.log('Current pathname:', location.pathname);

    const siteName = 'Institute Lab';
    const pathname = location.pathname;
    const is404FromWP = window.wpData?.is404;

    // Function to update both title and meta description
    const updatePageMeta = (title, description = null) => {
      console.log('Setting document title to:', title);
      document.title = title;

      if (description) {
        const metaDescription = document.querySelector('meta[name="description"]');
        if (metaDescription) {
          metaDescription.setAttribute('content', description);
        } else {
          // Create meta description if it doesn't exist
          const newMeta = document.createElement('meta');
          newMeta.name = 'description';
          newMeta.content = description;
          document.head.appendChild(newMeta);
        }
      }
    };

    // If WordPress detected this as 404, use that immediately
    if (is404FromWP) {
      updatePageMeta(
        `${t('meta.pageNotFound', 'Page Not Found')} - ${siteName}`,
        t('meta.description.notFound', 'The page you are looking for could not be found.')
      );
      return;
    }

    // Define route-specific titles and descriptions with translations
    const routeConfig = {
      '/': {
        title: `${siteName}`,
        description: t('meta.description.home', 'Discover our amazing products and shop with confidence')
      },
      '/products': {
        title: `${siteName}`,
        description: t('meta.description.products', 'Browse our complete collection of products')
      },
      '/shop-all': {
        title: `${siteName}`,
        description: t('meta.description.shopAll', 'Shop our entire product collection')
      },
      '/checkout': {
        title: `${siteName}`,
        description: t('meta.description.checkout', 'Complete your purchase securely')
      },
      '/order-received': {
        title: `${siteName}`,
        description: t('meta.description.orderReceived', 'Your order has been received successfully')
      },
      '/order-success': {
        title: `${siteName}`,
        description: t('meta.description.orderSuccess', 'Your order was completed successfully')
      },
      '/shipping': {
        title: `${siteName}`,
        description: t('meta.description.shipping', 'Learn about our shipping policies and delivery options')
      },
      '/terms': {
        title: `${siteName}`,
        description: t('meta.description.terms', 'Read our terms and conditions')
      },
      '/returns': {
        title: `${siteName}`,
        description: t('meta.description.returns', 'Learn about our returns and refund policy')
      },
      '/legal': {
        title: `${siteName}`,
        description: t('meta.description.legal', 'Legal information and policies')
      },
      '/paymentSuccessfull': {
        title: `${siteName}`,
        description: t('meta.description.paymentSuccess', 'Your payment was processed successfully')
      },
      '/paymentFailed': {
        title: `${siteName}`,
        description: t('meta.description.paymentFailed', 'There was an issue processing your payment')
      }
    };

    let config = routeConfig[pathname];

    // Handle dynamic routes
    if (!config) {
      if (pathname.startsWith('/product/') || pathname.startsWith('/store/product/')) {
        if (pathname.includes('/review')) {
          config = {
            title: `${siteName}`,
            description: t('meta.description.writeReview', 'Write a review for this product')
          };
        } else if (pathname.includes('/reviews')) {
          config = {
            title: `${siteName}`,
            description: t('meta.description.allReviews', 'Read all reviews for this product')
          };
        } else {
          // Extract product slug/id for a more specific title
          const productSlug = pathname.split('/product/')[1]?.split('/')[0] ||
            pathname.split('/store/product/')[1]?.split('/')[0];
          const productName = productSlug
            ? productSlug.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
            : t('meta.product', 'Product');

          config = {
            title: `${siteName}`,
            description: t('meta.description.product', `Learn more about ${productName}`)
          };
        }
      }
    }

    // If no config found, treat as 404
    if (!config) {
      config = {
        title: `${siteName}`,
        description: t('meta.description.notFound', 'The page you are looking for could not be found.')
      };
    }

    // Update the page meta
    updatePageMeta(config.title, config.description);

    // Add a small delay to ensure DOM is ready
  }, [location.pathname, t, i18n.language, currentLanguage]);
};

// Custom hook to detect if current route exists
const useRouteExists = () => {
  const location = useLocation();

  const validRoutes = [
    '/',
    '/products',
    '/shop-all',
    '/checkout',
    '/order-received',
    '/order-success',
    '/shipping',
    '/terms',
    '/returns',
    '/legal',
    '/paymentSuccessfull',
    '/paymentFailed'
  ];

  const isDynamicRoute = (path) => {
    return path.startsWith('/product/');
  };

  return validRoutes.includes(location.pathname) || isDynamicRoute(location.pathname);
};

// Wrapper component for page animations with loading state
const PageWrapper = ({ children, isLoading = false }) => (
  <motion.div
    initial="initial"
    animate={isLoading ? "initial" : "in"}
    exit="out"
    variants={pageVariants}
    transition={pageTransition}
    className="w-full"
  >
    {children}
  </motion.div>
);

const useIOSClickFix = () => {
  useEffect(() => {
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isIOS) return;

    const noop = () => { };
    document.addEventListener('touchstart', noop, { passive: true });
    return () => document.removeEventListener('touchstart', noop);
  }, []);
};

// Inner App component that has access to LanguageProvider
const AppContent = () => {
  const location = useLocation();
  const routeExists = useRouteExists();

  useIOSClickFix();
  useDocumentTitle(); // Now this hook can access LanguageProvider

  return (
    <CartProvider>
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow pt-[101px] overflow-hidden">
          <AnimatePresence mode="wait">
            <Routes location={location} key={location.pathname}>
              <Route path="/" element={<PageWrapper><HomePage /></PageWrapper>} />
              <Route path="/products" element={<PageWrapper><ProductGrid /></PageWrapper>} />
              <Route path="/store/product/:slug" element={<PageWrapper><ProductPage /></PageWrapper>} />
              <Route path="/product/:id" element={<PageWrapper><ProductPage /></PageWrapper>} />
              <Route path="/product/:id/review" element={<PageWrapper><ReviewPage /></PageWrapper>} />
              <Route path="/product/:id/reviews" element={<PageWrapper><AllReviewsPage /></PageWrapper>} />
              <Route path="/checkout" element={<PageWrapper><CheckoutPage /></PageWrapper>} />
              <Route path="/order-received" element={<PageWrapper><OrderReceived /></PageWrapper>} />
              <Route path="/order-success" element={<PageWrapper><OrderSuccessPage /></PageWrapper>} />
              <Route path="/shipping" element={<PageWrapper><ShippingPage /></PageWrapper>} />
              <Route path="/terms" element={<PageWrapper><TermsPage /></PageWrapper>} />
              <Route path="/returns" element={<PageWrapper><ReturnsPage /></PageWrapper>} />
              <Route path="/legal" element={<PageWrapper><LegalPage /></PageWrapper>} />
              <Route path="/shop-all" element={<PageWrapper><HomePage /></PageWrapper>} />
              <Route path="/paymentSuccessfull" element={<PageWrapper><PaymentSuccessPage /></PageWrapper>} />
              <Route path="/paymentFailed" element={<PageWrapper><PaymentFailedPage /></PageWrapper>} />
              {/* 404 Catch-all route */}
              <Route path="*" element={<PageWrapper><NotFoundPage /></PageWrapper>} />
            </Routes>
          </AnimatePresence>
        </main>
        <CartSidebar />
        <SearchSidebar />
        <MobileMenu />
        <Footer />
      </div>
    </CartProvider>
  );
};

// Main App component
function App() {
  return (
    <LanguageProvider>
      <CurrencyProvider>
        <AppContent />
        <CookieConsent />
      </CurrencyProvider>
    </LanguageProvider>
  );
}

export default App;